import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LoadingIndicator from '../../../components/ui/LoadingIndicator';

const RenderQueue = ({ currentRender, queuedRenders = [], onCancelRender, onRetryRender, loading = false }) => {
  const [expandedJob, setExpandedJob] = useState(null);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return { name: 'Loader2', className: 'animate-spin text-primary' };
      case 'pending':
        return { name: 'Clock', className: 'text-warning' };
      case 'completed':
        return { name: 'CheckCircle', className: 'text-success' };
      case 'failed':
        return { name: 'XCircle', className: 'text-error' };
      case 'cancelled':
        return { name: 'Ban', className: 'text-muted-foreground' };
      default:
        return { name: 'Circle', className: 'text-muted-foreground' };
    }
  };

  const getQualityBadge = (quality) => {
    const styles = {
      ultra: 'bg-purple-100 text-purple-800 border-purple-200',
      high: 'bg-green-100 text-green-800 border-green-200',
      medium: 'bg-blue-100 text-blue-800 border-blue-200',
      low: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles?.[quality] || styles?.medium;
  };

  const formatDuration = (startTime) => {
    if (!startTime) return 'Not started';
    const duration = Math.floor((Date.now() - new Date(startTime)?.getTime()) / 1000 / 60);
    if (duration < 1) return 'Just started';
    return `${duration} min ago`;
  };

  const getEstimatedTime = (status, progress = 0) => {
    if (status === 'pending') return 'Waiting in queue';
    if (status === 'processing' && progress > 0) {
      const remainingProgress = 100 - progress;
      const estimatedMinutes = Math.ceil(remainingProgress / 10); // Rough estimate
      return `~${estimatedMinutes} min remaining`;
    }
    if (status === 'failed') return 'Failed';
    if (status === 'cancelled') return 'Cancelled';
    return 'Processing...';
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingIndicator type="spinner" size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="List" size={20} className="text-warning" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Render Queue</h2>
            <p className="text-sm text-muted-foreground">
              {queuedRenders?.length || 0} jobs in queue
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCw">
          Refresh
        </Button>
      </div>

      <div className="space-y-4">
        {queuedRenders?.map((job) => {
          const statusIcon = getStatusIcon(job?.status);
          const isExpanded = expandedJob === job?.id;
          const estimatedTime = getEstimatedTime(job?.status, job?.progress);

          return (
            <div key={job?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  <Icon 
                    name={statusIcon?.name} 
                    size={20} 
                    className={statusIcon?.className} 
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-sm font-medium text-foreground truncate">
                        {job?.name || 'Untitled Render'}
                      </h3>
                      {job?.quality && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getQualityBadge(job?.quality)}`}>
                          {job?.quality}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {job?.project?.name || 'Unknown Project'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground hidden sm:block">
                    {estimatedTime}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedJob(isExpanded ? null : job?.id)}
                  >
                    <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
                  </Button>
                </div>
              </div>

              {/* Progress Bar */}
              {job?.status === 'processing' && typeof job?.progress === 'number' && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Progress</span>
                    <span>{job?.progress}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(100, Math.max(0, job?.progress))}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Error Message */}
              {job?.status === 'failed' && job?.error_message && (
                <div className="mt-3 p-2 bg-error/5 border border-error/20 rounded text-xs text-error">
                  {job?.error_message}
                </div>
              )}

              {/* Expanded Details */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <span className="text-xs text-muted-foreground">Quality</span>
                      <p className="text-sm font-medium text-foreground">{job?.quality || 'Medium'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Started</span>
                      <p className="text-sm font-medium text-foreground">
                        {job?.started_at ? formatDuration(job?.started_at) : 'Not started'}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Video File</span>
                      <p className="text-sm font-medium text-foreground">
                        {job?.video_file?.name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Job ID</span>
                      <p className="text-sm font-medium text-foreground font-mono">
                        {job?.id?.slice(0, 8)}...
                      </p>
                    </div>
                  </div>

                  {/* Settings Preview */}
                  {job?.settings && Object.keys(job?.settings)?.length > 0 && (
                    <div className="mb-4">
                      <span className="text-xs text-muted-foreground">Render Settings</span>
                      <div className="mt-1 p-2 bg-muted/30 rounded text-xs">
                        <pre className="text-foreground overflow-x-auto">
                          {JSON.stringify(job?.settings, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    {job?.status === 'processing' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="X"
                        onClick={() => onCancelRender?.(job?.id)}
                      >
                        Cancel
                      </Button>
                    )}
                    {job?.status === 'failed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="RotateCcw"
                        onClick={() => onRetryRender?.(job?.id)}
                      >
                        Retry
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" iconName="ExternalLink">
                      View Details
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(!queuedRenders || queuedRenders?.length === 0) && (
        <div className="text-center py-8">
          <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="text-sm font-medium text-foreground mb-1">No renders in queue</h3>
          <p className="text-xs text-muted-foreground">Start a new render to see it here</p>
        </div>
      )}
    </div>
  );
};

export default RenderQueue;